import type { TextFilterOperator } from '@/components/filters/filter-menu'
import type {
  FilterDefinition,
  UnifiedFilterOperator,
  UnifiedFilterValueOption,
} from '@/components/filters/list-filter-types'
import {
  formatTextFilterOperatorChip,
  formatUnifiedFilterOperatorChip,
  nextChipOperatorLabel,
} from '@/lib/list-filters/list-filter-params'

export type FilterOperatorFamily = 'enum' | 'text'

export type ResolvedFilterOperatorControls<TFilters> = {
  family: FilterOperatorFamily
  operators: readonly string[]
  getOperator: (filters: TFilters) => string
  setOperator: (filters: TFilters, op: string) => TFilters
  formatChip: (operator: string) => string
  nextOperator: (operator: string) => string
}

export function formatTextFilterOperatorPicker(operator: TextFilterOperator): string {
  switch (operator) {
    case 'is':
      return 'Is'
    case 'is_not':
      return 'Is not'
    case 'contains':
      return 'Contains'
    case 'does_not_contain':
      return 'Does not contain'
    default:
      return 'Contains'
  }
}

export function formatFilterOperatorChipLabel(
  family: FilterOperatorFamily,
  operator: string,
): string {
  if (family === 'text') {
    if (operator === 'contains' || operator === 'does_not_contain') {
      return formatTextFilterOperatorChip(operator)
    }
    return formatUnifiedFilterOperatorChip(operator as UnifiedFilterOperator)
  }
  return formatUnifiedFilterOperatorChip(operator as UnifiedFilterOperator)
}

export function nextFilterOperatorLabel(
  family: FilterOperatorFamily,
  operator: string,
  operators: readonly string[],
): string {
  if (family === 'text') {
    const index = operators.indexOf(operator)
    const next = index >= 0 ? operators[(index + 1) % operators.length] : operators[0]
    return formatFilterOperatorChipLabel('text', next ?? 'contains')
  }
  return nextChipOperatorLabel(operator)
}

export function resolveFilterOperatorControls<TFilters, TCtx>(
  def: FilterDefinition<TFilters, TCtx>,
): ResolvedFilterOperatorControls<TFilters> | null {
  const meta = def.unified
  if (!meta?.operators?.length || !meta.getOperator || !meta.setOperator) return null

  const family: FilterOperatorFamily = meta.operatorFamily ?? 'enum'
  return {
    family,
    operators: meta.operators,
    getOperator: meta.getOperator,
    setOperator: meta.setOperator,
    formatChip: (operator) => formatFilterOperatorChipLabel(family, operator),
    nextOperator: (operator) => nextFilterOperatorLabel(family, operator, meta.operators!),
  }
}

export function formatFilterSuggestionLabel<TFilters, TCtx>(
  def: FilterDefinition<TFilters, TCtx>,
  filters: TFilters,
  option?: UnifiedFilterValueOption,
): string {
  const controls = resolveFilterOperatorControls(def)
  if (option) {
    if (controls) {
      const operator = controls.formatChip(controls.getOperator(filters))
      return `${def.label} ${operator} ${option.label}`
    }
    return `${def.label} ${option.label}`
  }
  return def.label
}
